import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { fetchCampaignById } from '../../api/campaigns';
import { CampaignWorkspaceProvider } from './CampaignWorkspaceContext';
import { CampaignEmailHubProvider } from './email/CampaignEmailHubContext';
import CampaignSettingsPopover from './CampaignSettingsPopover';
import { useUserAccess } from '../../context/UserAccessContext';
import { canManageCrmCampaigns } from '../../utils/crmAccess';

function tabLinkClass(isActive) {
  return `inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
    isActive
      ? 'border-primary text-primary'
      : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
  }`;
}

export default function CampaignWorkspaceLayout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { user } = useUserAccess();
  const canManageCampaigns = canManageCrmCampaigns(user);

  const reloadCampaign = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchCampaignById(id);
      const c = res?.data?.campaign;
      if (!c) throw new Error('Campagne introuvable.');
      setCampaign(c);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Impossible de charger la campagne.');
      setCampaign(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    reloadCampaign();
  }, [reloadCampaign]);

  const base = `/campaigns/${encodeURIComponent(id || '')}`;
  const onEmailBranch = location.pathname.includes('/email');

  const workspaceValue = {
    campaign,
    campaignId: id || '',
    loading,
    error,
    reloadCampaign,
    canManageCampaigns,
  };

  const tagLabels = Array.isArray(campaign?.tags)
    ? campaign.tags.map((t) => (typeof t === 'object' && t?.name ? t.name : String(t)))
    : [];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{campaign?.name ? `${campaign.name} - Campagne` : 'Campagne - CRM'}</title>
      </Helmet>
      <Header />
      <Breadcrumb />
      <CampaignWorkspaceProvider value={workspaceValue}>
        <main className="main-content w-full max-w-none">
          <Button
            variant="ghost"
            size="sm"
            iconName="ArrowLeft"
            iconPosition="left"
            className="mb-4"
            onClick={() => navigate('/campaigns')}
          >
            Campagnes
          </Button>

          {loading ? (
            <p className="text-sm text-muted-foreground">Chargement…</p>
          ) : error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : (
            <>
              <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-start gap-2">
                    <h1 className="text-2xl font-semibold text-foreground md:text-3xl min-w-0">
                      {campaign?.name}
                    </h1>
                    {canManageCampaigns ? (
                      <button
                        type="button"
                        onClick={() => setSettingsOpen(true)}
                        className="shrink-0 rounded-lg p-2 pt-1 text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
                        aria-label="Paramètres de la campagne"
                      >
                        <Icon name="Settings" size={18} />
                      </button>
                    ) : null}
                  </div>
                  {campaign?.description ? (
                    <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                      {campaign.description}
                    </p>
                  ) : null}
                  {tagLabels.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {tagLabels.map((name) => (
                        <span
                          key={name}
                          className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  {tagLabels.length > 0 ? (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Users"
                      onClick={() => {
                        const q = tagLabels
                          .map((n) => `tags=${encodeURIComponent(n)}`)
                          .join('&');
                        navigate(`/prospect-contact-directory?${q}`);
                      }}
                    >
                      Voir les prospects
                    </Button>
                  ) : null}
                </div>
              </div>

              <nav
                className="-mx-1 mb-6 flex w-full border-b border-border overflow-x-auto"
                aria-label="Sections campagne"
              >
                <NavLink to={`${base}/products`} className={({ isActive }) => tabLinkClass(isActive)}>
                  <Icon name="Package" size={16} />
                  Produits
                </NavLink>
                <NavLink to={`${base}/hubup`} className={({ isActive }) => tabLinkClass(isActive)}>
                  <Icon name="Radio" size={16} />
                  HubUP
                </NavLink>
                <NavLink
                  to={`${base}/email`}
                  className={() =>
                    tabLinkClass(onEmailBranch || location.pathname.endsWith('/email'))
                  }
                >
                  <Icon name="Mail" size={16} />
                  Email
                </NavLink>
              </nav>

              {canManageCampaigns ? (
                <CampaignSettingsPopover
                  open={settingsOpen}
                  onClose={() => setSettingsOpen(false)}
                  campaign={campaign}
                  onSaved={(c) => {
                    if (c) setCampaign(c);
                    else reloadCampaign();
                  }}
                />
              ) : null}

              <div className="w-full min-w-0 pb-10">
                {onEmailBranch ? (
                  <CampaignEmailHubProvider
                    value={{
                      campaign,
                      campaignId: id,
                      reloadCampaign,
                      canManageCampaigns,
                    }}
                  >
                    <Outlet />
                  </CampaignEmailHubProvider>
                ) : (
                  <Outlet />
                )}
              </div>
            </>
          )}
        </main>
      </CampaignWorkspaceProvider>
    </div>
  );
}
